import { AlertTriangle as AlertTriangleDefault, ArrowRefresh2 } from 'blaze-ui/icons'
import Styles from './GenerationFailedPreview.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { useRegenerateBrandPlanContent } from 'hooks/queries/brandPlans/useRegenerateBrandPlanContent'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import cx from 'classnames'
import { Heading } from 'blaze-ui/Heading/Heading'
import { AlertTriangle as AlertTriangleLarge } from 'blaze-ui/icons/40'
import queryClient from 'queryClient'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { AlmanacFileCalendarItem } from 'hooks/queries/calendar/types'

type GenerationFailedPreviewProps = {
  fileId: string
  onClose?: () => void
  variant: 'eventCard' | 'fullScreenPreview'
}

export const GenerationFailedPreview = ({ fileId, variant, onClose }: GenerationFailedPreviewProps) => {
  const { data: brandPlan, isLoading: isLoadingBrandPlan } = useCurrentBrandPlan()

  const { mutateAsync: regenerateContent, isLoading: isLoadingRegenerateContent } = useRegenerateBrandPlanContent({
    onSuccess: () => {
      const queries = queryClient.getQueriesData<AlmanacFileCalendarItem[]>(calendarKeys.all)

      queries.forEach(([queryKey]) => {
        const existingData = queryClient.getQueryData(queryKey)
        if (!existingData || !Array.isArray(existingData)) return

        const newData = existingData.filter((item) => item.key !== fileId)
        if (newData.length !== existingData.length) {
          queryClient.setQueryData(queryKey, newData)
        }
      })
    },
  })

  const handleRetry = async () => {
    if (!brandPlan) return

    await regenerateContent({
      brandPlanId: brandPlan.id,
      body: { file_ids: [fileId], initial_media_file_ids: [], content_scope: 'user_selected_content' },
    })

    onClose?.()
  }

  const isLoading = isLoadingBrandPlan || isLoadingRegenerateContent

  return variant === 'eventCard' ? (
    <EventCardPreview onRetry={handleRetry} disableRetry={isLoading} />
  ) : (
    <FullScreenPreview onRetry={handleRetry} disableRetry={isLoading} />
  )
}

type PreviewProps = {
  onRetry: () => void
  disableRetry: boolean
}

const EventCardPreview = ({ onRetry, disableRetry }: PreviewProps) => {
  return (
    <div className={cx(Styles.root, Styles.eventCard)}>
      <div className={Styles.container}>
        <div className={Styles.description}>
          <AlertTriangleDefault size={24} color="var(--dark-80)" />
          <Text variant="secondary" fontWeight={'500'} color="var(--dark-80)">
            Generation Failed
          </Text>
        </div>
        <RetryButton onRetry={onRetry} disableRetry={disableRetry} />
      </div>
    </div>
  )
}

const FullScreenPreview = ({ onRetry, disableRetry }: PreviewProps) => {
  return (
    <div className={cx(Styles.root, Styles.fullScreenPreview)}>
      <div className={Styles.fullScreenDescription}>
        <AlertTriangleLarge size={40} color="var(--dark-80)" />
        <Heading level={5} color="var(--dark-80)">
          Generation Failed
        </Heading>
      </div>
      <RetryButton onRetry={onRetry} disableRetry={disableRetry} />
    </div>
  )
}

const RetryButton = ({ onRetry, disableRetry }: { onRetry: () => void; disableRetry: boolean }) => {
  return (
    <Button
      className={Styles.retryButton}
      variant="tertiary"
      size="sm"
      onPress={onRetry}
      frontIcon={ArrowRefresh2}
      isDisabled={disableRetry}
    >
      Retry
    </Button>
  )
}
